/*
XOL-467 — the licensee date chain, mirrored from the API's
`LicenseeRequest::withValidator()` (app/Http/Requests/LicenseeRequest.php,
`dar-es-salaam`):

    expiry        requires  startBilling
    startBilling  requires  started
    started       requires  handover      <- added bfa15d05, 2026-09-01

Read right to left: `handover` is the root and each field requires the one
behind it, PLUS two ordering rules:

    startBilling  must not be earlier than started   (equality allowed)
    expiry        must be strictly later than startBilling

`dateChainErrors(row, isCreate)` returns one entry per violated rule,
evaluated in the SAME order and with the SAME per-rule guards as the API, so
a row missing several links only ever reports the ONE nearest its existing
dates — the API does the same deliberately (LicenseeRequest.php:331-335: "it
puts two messages on a form when the user can only act on the first").

`isCreate` (XOL-483, reviewer finding 1) — on POST, the API's
`prepareForValidation()` (LicenseeRequest.php:71-79) runs
`materialiseCommencementFromHandover()` then
`defaultBillingStartFromCommencement()` BEFORE `withValidator()` ever sees
the row: `started` defaults from `handover`, then `startBilling` defaults
from (the now-defaulted) `started`, in each case only when the caller didn't
supply their own value. Judging the RAW form values on create checks a row
the API never constructs — the New Sharer form's Agreement Start + Tenor
auto-derive a non-null Expiry with no Billing Start input at all, which is
exactly the shape the cascade exists to make valid. Passing `isCreate: true`
applies the same two-step cascade to a local copy of the row before the
rules below run. The update path is untouched: omit the argument (or pass a
falsy value) and this is exactly the pre-XOL-483 behaviour.

Returns CODES, not sentences — copy is written per surface. ProjectTab has no
`expiry` input and Site -> Financials has no `handover` input, so a rule
whose triggering field a surface cannot render has to reroute its message to
a field it can, with different wording. See each component's own
`dateChainCopy()` / equivalent.

ES5-safe: no `?.`, no `??` (Babel 6 — see the repo's CLAUDE.md).
------------------------------------------------------------------------ */

// Sentinels that mean "no date" — mirrors LicenseeRequest::resolvedDate()'s
// is_string() guard plus DateAnchor::normalize() treating '0000-00-00' as null.
function isAbsent (value) {
  return value === null || value === undefined || value === '' || value === '0000-00-00'
}

function isPresent (value) {
  return !isAbsent(value)
}

/**
 * @param {Object} row - { handover, started, startBilling, expiry }. Each a
 *   'YYYY-MM-DD' wire-format string (or one of the absent sentinels above).
 *   Comparisons are lexicographic, which is safe only for that exact shape —
 *   callers must normalise (formatDateSql / the date picker's value-format)
 *   before calling this.
 * @param {Boolean} [isCreate] - mirrors the API's create-only cascade
 *   (`started ||= handover`, then `startBilling ||= started`) before
 *   evaluating the rules below. Only the two create surfaces (LicenseeForm,
 *   SharerAction) should pass true; ProjectTab / FinanceSharer always PUT
 *   and must not.
 * @returns {Array<{field: String, code: String}>} in API-priority order.
 */
export function dateChainErrors (row, isCreate) {
  var r = row || {}
  var handover = r.handover
  var started = r.started
  var startBilling = r.startBilling
  var expiry = r.expiry

  // XOL-483 — create-only cascade, mirroring
  // LicenseeRequest::materialiseCommencementFromHandover() then
  // ::defaultBillingStartFromCommencement(). ORDER IS LOAD-BEARING, same as
  // the API: started must be resolved first so the startBilling default sees
  // the value the first step produced.
  if (isCreate) {
    if (isAbsent(started) && isPresent(handover)) {
      started = handover
    }

    if (isAbsent(startBilling) && isPresent(started)) {
      startBilling = started
    }
  }

  var errors = []

  // THE CHAIN — nearest link only. Each guard is keyed on the presence of
  // ONE specific pair, so these conditions are naturally mutually exclusive
  // on any row missing exactly one link: a row with `started` but no
  // `handover` trips ONLY the first one below, never also the third (which
  // requires `started` to be ABSENT).
  if (isPresent(started) && isAbsent(handover)) {
    errors.push({ field: 'handover', code: 'handover_required' })
  }

  if (isPresent(expiry) && isAbsent(startBilling)) {
    errors.push({ field: 'startBilling', code: 'billing_start_required' })
  }

  if (isPresent(startBilling) && isAbsent(started)) {
    errors.push({ field: 'started', code: 'started_required' })
  }

  // ORDERING — equality allowed: billing opening on the commencement date is
  // the normal case.
  if (isPresent(startBilling) && isPresent(started) && startBilling < started) {
    errors.push({ field: 'startBilling', code: 'billing_before_started' })
  }

  // ORDERING — STRICT, unlike the rule above: an agreement ending the day
  // billing opens bills nothing at all, so equality is rejected here.
  if (isPresent(expiry) && isPresent(startBilling) && expiry <= startBilling) {
    errors.push({ field: 'expiry', code: 'expiry_before_billing_start' })
  }

  return errors
}

// XOL-467 (any-to-all widening) — shared auto-fill mechanics for all four
// date-chain surfaces (ProjectTab, FinanceSharer, SharerAction, LicenseeForm).
//
// Carlos's original instruction: "If handover, started, and startBilling are
// not set, and the user sets one of them, auto fill the other dates also."
// That is symmetric — ANY of the family fields can be the source — which is
// why this lives here instead of as a Handover-only method on ProjectTab (the
// pre-widening shape, `maybeAutoFillFromHandover`, only ever treated Handover
// as a source and never a target).
//
// Gate: PER FIELD. Every sibling that is individually absent gets filled;
// siblings that already hold a value are left alone.
//
// It was previously all-or-nothing — fire only when EVERY other field is
// absent — which produced the bug Carlos reported on 2026-09-02: with Billing
// Start already set and the other two empty, filling Agreement Start left
// Handover empty, because the all-others-absent test saw Billing Start and
// bailed on the whole row. Filling the fields the user can see are still
// blank is the point of the feature, so the gate is now per field.
//
// The guarantee that survives is the one that matters: a value the user
// typed is NEVER overwritten, because only absent fields are targets.
//
// Consequence, accepted deliberately: clearing an auto-filled field and then
// editing a sibling will re-fill it, where the old gate would not have. That
// is the same rule read consistently — an empty field gets filled when you
// set a related date — and it is what makes the reported case work.
//
// @param {Object} row - current field values (e.g. `details` or `telco`).
// @param {String} changedField - the field the user just set; must be a
//   member of `family` or this is a no-op (e.g. `expiry`/`onAir` changing on
//   a surface that doesn't include them in `family`).
// @param {*} value - the NEW value of `changedField` (passed explicitly,
//   rather than read off `row[changedField]`, so callers don't have to
//   guarantee `row` was already mutated by v-model before this runs).
// @param {Array<String>} family - the fields this surface owns, e.g.
//   `['handover', 'started', 'startBilling']` (ProjectTab, SharerAction,
//   LicenseeForm) or `['started', 'startBilling']` (FinanceSharer, which has
//   no Handover input — Handover is neither source nor target there).
// @returns {Object} map of field -> value for the siblings to fill. Contains
//   only the siblings that were absent, so it is empty when they all already
//   hold values; callers should treat an empty result as "nothing to do"
//   (no autoFilled flag, no mutation).
function autoFillDates (row, changedField, value, family) {
  var updates = {}
  var r = row || {}
  if (!family || family.indexOf(changedField) === -1) return updates
  if (isAbsent(value)) return updates

  family.forEach(function (f) {
    if (f === changedField) return
    if (isAbsent(r[f])) updates[f] = value
  })
  return updates
}

export { isAbsent, isPresent, autoFillDates }

export default {
  dateChainErrors: dateChainErrors,
  isAbsent: isAbsent,
  isPresent: isPresent,
  autoFillDates: autoFillDates
}
